import { ZonedDateTimeFactory, getTimeZoneId } from '@csp/csp-common-date-util';
import { v4 as uuid } from 'uuid';
import { UsageTrackerTrackEvent, UsageTrackerPageEvent, UsageTrackerIdentifyEvent } from '@csp/csp-common-analytics';
import { Maybe } from '@csp/csp-common-model';
import { BaseEvent } from '../model/BaseEvent';
import { NavigatorUAData } from '../model/NavigatorUAData';
import { AnalyticsEventOptions, AnalyticsIdentifyOptions } from '../model/AnalyticsClient';
import { PageContext } from './PageContext';
import { UtmCampaign } from './UtmCampaign';

const TRACK_EVENT_VERSION = 1 as const;
const PAGE_EVENT_VERSION = 1 as const;
const IDENTIFY_EVENT_VERSION = 1 as const;

const getTimezone = (): Maybe<string> => {
  try {
    return getTimeZoneId();
  } catch {
    return undefined;
  }
};

const createBaseEvent = (anonymousId = '', locale = ''): BaseEvent => {
  const timestamp = ZonedDateTimeFactory.now();
  const userAgentData = (window.navigator as unknown as { userAgentData: Maybe<NavigatorUAData> }).userAgentData;
  const page = PageContext.create();
  const campaign = page.search ? UtmCampaign.get(page.search) : undefined;

  return {
    anonymousId,
    messageId: uuid(),
    timestamp,
    context: {
      campaign,
      locale: locale || window.navigator.language,
      page,
      screen: {
        height: window.innerHeight,
        width: window.innerWidth,
      },
      timezone: getTimezone(),
      userAgent: window.navigator.userAgent,
      userAgentData: userAgentData?.toJSON(),
    },
  };
};

const createTrackEvent = (eventName: string, options?: AnalyticsEventOptions): UsageTrackerTrackEvent => {
  const { userId, anonymousId, properties, locale } = options ?? { properties: {} };
  const baseEvent = createBaseEvent(anonymousId, locale);

  return {
    ...baseEvent,
    type: 'track',
    event: eventName,
    properties,
    version: TRACK_EVENT_VERSION,
    userId,
  };
};

const createPageEvent = (pageName: string, options?: AnalyticsEventOptions): UsageTrackerPageEvent => {
  const { userId, anonymousId, properties, locale } = options ?? { properties: {} };
  const baseEvent = createBaseEvent(anonymousId, locale);

  return {
    ...baseEvent,
    type: 'page',
    name: pageName,
    properties: { ...properties, ...baseEvent.context.page, name: pageName },
    version: PAGE_EVENT_VERSION,
    userId,
  };
};

const createIdentifyEvent = (userId: string, options?: AnalyticsIdentifyOptions): UsageTrackerIdentifyEvent => {
  const { anonymousId, properties, locale } = options ?? { properties: {} };

  const baseEvent = createBaseEvent(anonymousId, locale);
  return {
    ...baseEvent,
    type: 'identify',
    properties,
    version: IDENTIFY_EVENT_VERSION,
    userId,
  };
};

export const AnalyticsEventFactory = {
  createTrackEvent,
  createPageEvent,
  createIdentifyEvent,
};
